// @owlcat/app-shell — fleet-wide cookie consent primitives.
//
// ── ONE consent for the whole owlcat fleet ──────────────────────────────────
// Every owlcat app is a subdomain of the SAME parent domain and is operated by
// the SAME legal controller (Owl & Cat), presenting the SAME cookie categories
// and the SAME third-party vendors. Under those three conditions a SINGLE
// consent, stored on the PARENT domain, is legally valid across every sibling
// app (GDPR/ePrivacy: one controller, one purpose set, one vendor set → one
// consent). If any app ever ships a DISTINCT analytics/marketing vendor, that
// vendor needs its OWN category and this shared banner must surface it — do not
// silently reuse the `analytics` bucket for a new vendor. See PRIVACY-NOTE below.
//
// We mirror @owlcat/theme's ThemeCookieSync: write the choice into a cookie on
// `.<NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN>` (Secure, SameSite=Lax, ~12mo) so every
// sibling subdomain reads the same value on the next cross-app hop. Host-only on
// bare localhost (parent domain unset).
//
// Categories (fleet-aligned):
//   essential  — always on, cannot be refused. owlcat-id session (RS256 cookie),
//                next-auth.* (shop/portal auth), NEXT_LOCALE, owlcat-consent,
//                owlcat-theme/owlcat-locale. Strictly necessary to deliver the
//                service the user asked for → no consent required, always true.
//   functional — remembered UI preferences beyond the strictly-necessary set
//                (game settings, non-essential UI state). Opt-in.
//   analytics  — product analytics (PostHog, portal only today). Opt-in.
//
// PRIVACY-NOTE (for the privacy policy): the consent set here covers the fleet
// subdomains accounts.owlcat.*, owlcat.* (portal), games.*, habits.*, film.*,
// shop.*/store.* and the landing site, all under controller "Owl & Cat". Purposes
// = strictly-necessary auth/session, functional preferences, and product
// analytics via PostHog. Live-support chat (Tawk.to, portal) is a DISTINCT vendor
// and is FLAGGED: it is NOT covered by these three categories and must be granted
// through its own category before loading (kept OFF until then).
export const CONSENT_COOKIE = "owlcat-consent";
/** Bump when the categories/vendors/policy change → every app re-prompts. */
export const CONSENT_VERSION = 1;
/** ~12 months (matches owlcat-theme/owlcat-locale). */
export const CONSENT_MAX_AGE = 60 * 60 * 24 * 365;
/** Dispatched on the window after a choice is written (same-tab live update). */
export const CONSENT_CHANGE_EVENT = "owlcat-consent-change";
/** Dispatched to force-open the banner (e.g. a footer "Cookie settings" link). */
export const CONSENT_OPEN_EVENT = "owlcat-consent:open";
function isBrowser() {
    return typeof document !== "undefined";
}
/** Parse a raw cookie value into a validated record, or null if unusable/stale. */
export function parseConsent(raw) {
    if (!raw)
        return null;
    try {
        const obj = JSON.parse(decodeURIComponent(raw));
        if (!obj || typeof obj !== "object")
            return null;
        if (typeof obj.v !== "number")
            return null;
        // Older schema than the current version → treat as no consent (re-prompt).
        if (obj.v < CONSENT_VERSION)
            return null;
        const c = obj.categories;
        if (!c || typeof c !== "object")
            return null;
        return {
            v: obj.v,
            ts: typeof obj.ts === "string" ? obj.ts : new Date().toISOString(),
            categories: {
                ...c,
                essential: true,
                functional: c.functional === true,
                analytics: c.analytics === true,
            },
        };
    }
    catch {
        return null;
    }
}
function readRawCookie(name) {
    if (!isBrowser())
        return null;
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? m[1] : null;
}
/**
 * Current consent, or null when the user has NOT yet made a valid choice for the
 * current CONSENT_VERSION (→ the banner must prompt). SSR-safe (null on server).
 */
export function readConsent() {
    return parseConsent(readRawCookie(CONSENT_COOKIE));
}
/** True when a valid, current-version consent exists. */
export function hasStoredConsent() {
    return readConsent() !== null;
}
/**
 * Is `category` allowed? `essential` is ALWAYS true. Optional categories require
 * a stored grant; absent consent → only essential is allowed.
 */
export function hasConsent(category) {
    if (category === "essential")
        return true;
    const rec = readConsent();
    return rec ? rec.categories[category] === true : false;
}
/** Cookie `Domain=` attribute for the parent domain, or "" for host-only. */
function domainAttr(parentDomain, host) {
    const parent = parentDomain?.trim().replace(/^\.+/, "");
    if (!parent || !host)
        return "";
    const onParent = host === parent || host.endsWith(`.${parent}`);
    return onParent ? `; Domain=.${parent}` : "";
}
/**
 * PURE: build the `document.cookie` assignment string for a consent record.
 * Exposed for testing and for apps that persist extra categories (e.g. portal's
 * Tawk `marketing` key) while keeping byte-identical formatting.
 */
export function buildConsentCookie(record, opts = {}) {
    const value = encodeURIComponent(JSON.stringify(record));
    const domain = domainAttr(opts.parentDomain, opts.host);
    const secure = opts.secure ? "; Secure" : "";
    const maxAge = opts.maxAge ?? CONSENT_MAX_AGE;
    return `${CONSENT_COOKIE}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${domain}${secure}`;
}
/** Build a full consent record from a (partial) categories choice. */
export function toRecord(categories) {
    return {
        v: CONSENT_VERSION,
        ts: new Date().toISOString(),
        categories: {
            ...categories,
            essential: true,
            functional: categories.functional === true,
            analytics: categories.analytics === true,
        },
    };
}
/**
 * Persist a consent choice to the shared parent-domain cookie and notify the
 * current tab (dispatches CONSENT_CHANGE_EVENT). Returns the stored record.
 */
export function writeConsent(categories, opts = {}) {
    const record = toRecord(categories);
    if (isBrowser()) {
        const host = opts.host ?? window.location.hostname;
        const secure = opts.secure ?? window.location.protocol === "https:";
        document.cookie = buildConsentCookie(record, { ...opts, host, secure });
        window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: record }));
    }
    return record;
}
/** Subscribe to consent changes (same-tab). Returns an unsubscribe fn. */
export function onConsentChange(cb) {
    if (!isBrowser())
        return () => { };
    const handler = (e) => cb(e.detail ?? toRecord({}));
    window.addEventListener(CONSENT_CHANGE_EVENT, handler);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}
/** Re-open the consent banner from anywhere (e.g. a footer "Cookie settings"). */
export function openConsentBanner() {
    if (isBrowser())
        window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}
